import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../store/store';
import { INVEST_PROFILE_FIELDS, COMPANY_PROFILE_FIELDS } from '../../utils/constants/fields';
import { formatPercent, formatNumber, formatNumberRange } from '../../utils/utils';
import * as Portal from '@radix-ui/react-portal';
import DetailsContactTab from './DetailsContactTab';
import { PUNCTUATION } from '../../utils/constants/notation';
import ChipsList from '../common/ChipsList';

const InvestProfileDetailsPopup = observer(
  ({ profile = {}, onClose = () => {}, contact = null }) => {
    const { utilsStore } = useStore();
    const state = useLocalObservable(() => ({
      tab: contact ? 'contact' : 'general',
      setTab: (tab = 'general') => (state.tab = tab)
    }));

    const tabs = [
      ...(contact
        ? [
            {
              name: 'Contact',
              onClick: () => state.setTab('contact'),
              isActive: state.tab === 'contact'
            }
          ]
        : []),
      {
        name: 'General information',
        onClick: () => state.setTab('general'),
        isActive: state.tab === 'general'
      },
      {
        name: 'Profile information',
        onClick: () => state.setTab('information'),
        isActive: state.tab === 'information'
      }
    ];

    const companyName =
      profile.companyName || profile.companyProfile?.[COMPANY_PROFILE_FIELDS.COMPANY_NAME.NAME];

    return (
      <Portal.Root>
        <div className="shared-popup">
          <div className="shared-fund-details-popup-body">
            <div className="header">
              <div className="tabs">
                {tabs.map((tab) => {
                  return (
                    <div
                      key={tab.name}
                      className={`tab${tab.isActive ? ' active' : ''}`}
                      onClick={tab.onClick}>
                      {tab.name}
                    </div>
                  );
                })}
              </div>
              <div className="close" onClick={onClose}>
                Close
              </div>
            </div>
            <div className="content">
              {state.tab === 'contact' && <DetailsContactTab contact={contact} />}
              {state.tab === 'general' && (
                <>
                  {!!companyName && (
                    <div className="row">
                      <div className="bullet"></div>
                      <div className="label">Company name</div>
                      <div className="value">{companyName}</div>
                    </div>
                  )}
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.NAME.LABEL}</div>
                    <div className="value">
                      {profile[INVEST_PROFILE_FIELDS.NAME.NAME] || PUNCTUATION.EMPTY_VALUE}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.REVENUE.LABEL} (€M)</div>
                    <div className="value">
                      {formatNumber(profile[INVEST_PROFILE_FIELDS.REVENUE.NAME])}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.EBITDA.LABEL} (€M)</div>
                    <div className="value">
                      {formatNumber(profile[INVEST_PROFILE_FIELDS.EBITDA.NAME])}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.DESCRIPTION.LABEL}</div>
                    <div className="value">{profile[INVEST_PROFILE_FIELDS.DESCRIPTION.NAME]}</div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.INDUSTRIES.LABEL}</div>
                    <div className="value">
                      {/* {utilsStore
                        .getOptionName(profile, INVEST_PROFILE_FIELDS.INDUSTRIES)
                        .join(', ')} */}
                      <ChipsList
                        list={utilsStore.getOptionName(profile, INVEST_PROFILE_FIELDS.INDUSTRIES)}
                        visibleCount={88888}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.GEOGRAPHIES.LABEL}</div>
                    <div className="value">
                      {/* {utilsStore
                        .getOptionName(profile, INVEST_PROFILE_FIELDS.GEOGRAPHIES)
                        .join(', ')} */}
                      <ChipsList
                        list={utilsStore.getOptionName(profile, INVEST_PROFILE_FIELDS.GEOGRAPHIES)}
                        visibleCount={88888}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.SITUATION.LABEL}</div>
                    <div className="value">
                      {/* {utilsStore
                        .getOptionName(profile, INVEST_PROFILE_FIELDS.SITUATION)
                        .join(', ')} */}
                      <ChipsList
                        list={utilsStore.getOptionName(profile, INVEST_PROFILE_FIELDS.SITUATION)}
                        visibleCount={88888}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.EQUITY.LABEL}</div>
                    <div className="value">
                      {/* {utilsStore.getOptionName(profile, INVEST_PROFILE_FIELDS.EQUITY).join(', ')} */}
                      <ChipsList
                        list={utilsStore.getOptionName(profile, INVEST_PROFILE_FIELDS.EQUITY)}
                        visibleCount={88888}
                      />
                    </div>
                  </div>
                </>
              )}
              {state.tab === 'information' && (
                <>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">
                      {INVEST_PROFILE_FIELDS.REVENUE_RANGE_MIN.LABEL} (€M)
                    </div>
                    <div className="value">
                      {formatNumberRange(
                        profile[INVEST_PROFILE_FIELDS.REVENUE_RANGE_MIN.NAME],
                        profile[INVEST_PROFILE_FIELDS.REVENUE_RANGE_MAX.NAME]
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.EBITDA_RANGE_MIN.LABEL} (€M)</div>
                    <div className="value">
                      {formatNumberRange(
                        profile[INVEST_PROFILE_FIELDS.EBITDA_RANGE_MIN.NAME],
                        profile[INVEST_PROFILE_FIELDS.EBITDA_RANGE_MAX.NAME]
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.EBITDA_MARGIN.LABEL}</div>
                    <div className="value">
                      {formatPercent(profile[INVEST_PROFILE_FIELDS.EBITDA_MARGIN.NAME])}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">
                      {INVEST_PROFILE_FIELDS.ENTERPRISE_RANGE_MIN.LABEL} (€M)
                    </div>
                    <div className="value">
                      {formatNumberRange(
                        profile[INVEST_PROFILE_FIELDS.ENTERPRISE_RANGE_MIN.NAME],
                        profile[INVEST_PROFILE_FIELDS.ENTERPRISE_RANGE_MAX.NAME]
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="bullet"></div>
                    <div className="label">{INVEST_PROFILE_FIELDS.EQUITY_RANGE_MIN.LABEL} (€M)</div>
                    <div className="value">
                      {formatNumberRange(
                        profile[INVEST_PROFILE_FIELDS.EQUITY_RANGE_MIN.NAME],
                        profile[INVEST_PROFILE_FIELDS.EQUITY_RANGE_MAX.NAME]
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Portal.Root>
    );
  }
);

export default InvestProfileDetailsPopup;
